/**
 * Single source of truth for "Total Budget" calculation.
 * MUST stay in sync with the Budgets tab (BudgetList.js) so the dashboard
 * and the budgets page never disagree.
 *
 * Total budget = Σ over months × Σ over categories of:
 *   - For shared categories: category.budget (joined limit)
 *   - For owned categories: category.monthlyBudgets[YYYY-MM] (or legacy category.budget if no monthlyBudgets)
 *   + rollover (category.rolloverData[YYYY-MM].rolloverAmount, if not dismissed)
 */

export function getCategoryMonthlyBudget(category, monthKey) {
  if (!category) return 0;

  // Shared categories use the joined per-user limit
  if (category.isShared && category.sharedCategoryId) {
    return Number(category.budget || 0);
  }

  let baseAmount = 0;
  if (category.monthlyBudgets && typeof category.monthlyBudgets === 'object') {
    const monthlyAmount = category.monthlyBudgets[monthKey];
    if (monthlyAmount !== undefined && monthlyAmount !== null) {
      baseAmount = Number(monthlyAmount) || 0;
    }
    // If month not present in monthlyBudgets, baseAmount stays 0 (true month independence)
  } else {
    // Legacy fallback: only when category was never migrated to monthlyBudgets
    baseAmount = Number(category.budget || 0);
  }

  const rolloverData = category.rolloverData?.[monthKey];
  const rolloverAmount = (rolloverData && !rolloverData.dismissed)
    ? (Number(rolloverData.rolloverAmount) || 0)
    : 0;

  return baseAmount + rolloverAmount;
}

export function monthKeyOf(year, monthIndexZeroBased) {
  return `${year}-${String(monthIndexZeroBased + 1).padStart(2, '0')}`;
}

export function monthKeysBetween(startDate, endDate) {
  const keys = [];
  const cur = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  while (cur <= end) {
    keys.push(monthKeyOf(cur.getFullYear(), cur.getMonth()));
    cur.setMonth(cur.getMonth() + 1);
  }
  return keys;
}

/**
 * Total budget across one or more month keys.
 */
export function getTotalBudgetForMonthKeys(categories, monthKeys) {
  if (!Array.isArray(categories) || !Array.isArray(monthKeys)) return 0;
  let total = 0;
  for (const monthKey of monthKeys) {
    for (const cat of categories) {
      total += getCategoryMonthlyBudget(cat, monthKey);
    }
  }
  return total;
}

/**
 * Convenience: total budget for the current dashboard view.
 *
 * @param {Object} opts
 * @param {Array}   opts.categories
 * @param {boolean} opts.isRangeMode
 * @param {Date}   [opts.rangeStart]
 * @param {Date}   [opts.rangeEnd]
 * @param {number} [opts.year]
 * @param {number} [opts.monthIndex]  0-based month index
 */
export function getDashboardTotalBudget({
  categories,
  isRangeMode,
  rangeStart,
  rangeEnd,
  year,
  monthIndex,
}) {
  const keys = isRangeMode && rangeStart && rangeEnd
    ? monthKeysBetween(rangeStart, rangeEnd)
    : [monthKeyOf(year, monthIndex)];
  return getTotalBudgetForMonthKeys(categories, keys);
}
